import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Paginator } from "../../Common/_components/Paginator";
import { CustomSearchBar, MainPageWithButtons } from "../_components";
import { CapsuleButton } from "../_components/CapsuleButton";
import { CustomModal } from "../_components/CustomModal";
import { WaitSpinner } from "../_components/WaitSpinner";
import { invitationGroupActions, parkingActions } from "../_actions";
import { BulkCreateResult } from "../_actions/parking.actions";
import { appPermissions } from "../_constants/permissions.constants";
import { Selectors } from "../_reducers/app.reducer";
import { ParkingFilter } from "../_models/ParkingSpot";
import { SingleParkingSpot } from "./_components/SingleParkingSpot";
import { ParkingFilters } from "./_components/ParkingFilters";
import { ParkingGroupConfig } from "./_components/ParkingGroupConfig";
import { NewParkingSpot, NewParkingSpotDraft } from "./NewParkingSpot";
import { parseParkingCsv, ParsedSpotRow } from "./_helpers/parseParkingCsv";
import { Icons } from "../../Common/_assets/icons/Icons";
import settingsIcon from "../_assets/appIcons/icon_module_settings.svg";
import "./ParkingListView.scss";

const EMPTY_DRAFT: NewParkingSpotDraft = {
    floor: "1",
    numberRaw: "",
    holderName: "",
    holderEmail: "",
    maxAssignments: "1",
    notes: "",
};

// Transparent toggle so we can render the "Ajustes" CapsuleButton as the
// dropdown trigger while react-bootstrap handles the menu open/close state.
const SettingsDropdownToggle = forwardRef<HTMLDivElement, any>(
    ({ children, onClick }, ref) => (
        <div
            ref={ref}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
        >
            {children}
        </div>
    ),
);

export function ParkingListView() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const locationUid = useSelector(Selectors.getSelectedLocationUid);
    const locationInfo = useSelector(Selectors.getLocationInfo);
    const spots = useSelector(Selectors.getParkingSpots);
    const filter = useSelector(Selectors.getParkingFilter);
    const floors = useSelector(Selectors.getParkingFloors);
    const offset = useSelector(Selectors.getParkingOffset);
    const pageSize = useSelector(Selectors.getParkingPageSize);
    const totalCount = useSelector(Selectors.getParkingTotalCount);
    const isRequesting = useSelector(Selectors.getIsRequestingParking);
    const selectedUid = useSelector(Selectors.getSelectedParkingSpotUid);
    const invitationGroupUid = useSelector(
        Selectors.getParkingInvitationGroupUid,
    );
    const configLoaded = useSelector(Selectors.getParkingConfigLoaded);
    const invitationGroups = useSelector(Selectors.getInvitationGroups) ?? [];
    const rooms = (useSelector(Selectors.getRooms) ?? []) as Array<{
        uid: string;
        name: string;
    }>;

    const currentUserEmail = useSelector(Selectors.getUserEmail);
    const isAdmin = appPermissions.canUserManageParking(
        locationInfo?.locationPermission,
    );
    const canManage = isAdmin;
    const hasGroupConfigured = !!invitationGroupUid;
    const hasActiveFilters =
        !!filter.searchTerm ||
        !!filter.floor ||
        (!!filter.status && filter.status !== "ALL");

    const listRef = useRef<HTMLDivElement>(null);
    const csvInputRef = useRef<HTMLInputElement>(null);

    const [showNewModal, setShowNewModal] = useState(false);
    const [draft, setDraft] = useState<NewParkingSpotDraft>(EMPTY_DRAFT);

    const [showImportModal, setShowImportModal] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importResult, setImportResult] = useState<
        (BulkCreateResult & { unparsed: number }) | null
    >(null);
    // Rows staged for preview before the user confirms the import. `null`
    // means no preview is open; `[]` means parsing produced nothing valid
    // (we skip the preview and go straight to the result modal).
    const [previewRows, setPreviewRows] = useState<ParsedSpotRow[] | null>(
        null,
    );
    const [previewUnparsed, setPreviewUnparsed] = useState(0);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    // Incrementing this opens the group-config modal from the settings menu
    // (ParkingGroupConfig watches it via its `openSignal` prop). Starts
    // undefined so the child's effect does not fire on first render.
    const [groupModalSignal, setGroupModalSignal] = useState<number | undefined>(
        undefined,
    );

    const [searchInput, setSearchInput] = useState<string>(
        filter.searchTerm ?? "",
    );
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Keep the local input in sync when the filter changes externally
        // (e.g., switching role or resetting filters). Avoid overriding while
        // the user is actively typing by only syncing when values differ.
        setSearchInput((prev) =>
            prev === (filter.searchTerm ?? "") ? prev : filter.searchTerm ?? "",
        );
    }, [filter.searchTerm]);

    const fetchSpots = useCallback(
        (nextOffset: number, nextFilter: ParkingFilter) => {
            if (!locationUid) return;
            dispatch(
                parkingActions.listSpots({
                    locationUid,
                    offset: nextOffset,
                    pageSize,
                    filter: nextFilter,
                }),
            );
        },
        [dispatch, locationUid, pageSize],
    );

    useEffect(() => {
        fetchSpots(offset, filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationUid]);

    useEffect(() => {
        if (!locationUid || !isAdmin) return;
        dispatch(parkingActions.loadConfig(locationUid));
        dispatch(invitationGroupActions.getGroupsForLocation(locationUid));
    }, [dispatch, locationUid, isAdmin]);

    const onChangeFilter = (patch: Partial<ParkingFilter>) => {
        const nextFilter = { ...filter, ...patch };
        dispatch(parkingActions.changeFilter(patch));
        dispatch(parkingActions.resetPagination());
        fetchSpots(0, nextFilter);
    };

    const onSearchInput = (value: string) => {
        setSearchInput(value);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            onChangeFilter({ searchTerm: value });
        }, 400);
    };

    const onChangePage = (nextOffset: number) => {
        listRef.current?.scrollIntoView({ behavior: "smooth" });
        dispatch(parkingActions.changeOffset(nextOffset));
        fetchSpots(nextOffset, filter);
    };

    const openNewModal = () => {
        setDraft({ ...EMPTY_DRAFT, floor: floors[0] ?? "1" });
        setShowNewModal(true);
    };

    const isDraftValid =
        draft.floor.trim().length > 0 && draft.numberRaw.trim().length > 0;

    const handleCsvFileChange = async (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = e.target.files?.[0];
        // Reset so selecting the same file twice re-triggers change
        if (csvInputRef.current) csvInputRef.current.value = "";
        if (!file || !locationUid) return;

        setShowImportModal(false);
        const text = await file.text();
        const { rows, skipped: unparsed } = parseParkingCsv(text);
        if (rows.length === 0) {
            // Nothing to preview — go straight to the result modal so the
            // user sees the "unparsed" count.
            setImportResult({
                created: 0,
                reused: 0,
                skipped: 0,
                errors: 0,
                unparsed,
            });
            return;
        }
        setPreviewRows(rows);
        setPreviewUnparsed(unparsed);
    };

    const closePreview = () => {
        setPreviewRows(null);
        setPreviewUnparsed(0);
    };

    const confirmPreviewImport = async () => {
        if (!locationUid || !previewRows) return;
        const rows = previewRows;
        const unparsed = previewUnparsed;
        closePreview();
        setIsImporting(true);
        try {
            const result = (await dispatch(
                parkingActions.bulkCreateSpots({
                    locationUid,
                    rows,
                }),
            )) as unknown as BulkCreateResult;
            setImportResult({ ...result, unparsed });
        } finally {
            setIsImporting(false);
        }
    };

    const submitNewSpot = () => {
        if (!locationUid || !isDraftValid) return;
        const maxParsed = parseInt(draft.maxAssignments, 10);
        const maxAssignments =
            Number.isFinite(maxParsed) && maxParsed >= 0 ? maxParsed : 1;
        dispatch(
            parkingActions.createSpot({
                locationUid,
                floor: draft.floor.trim(),
                numberRaw: draft.numberRaw.trim(),
                holderName: draft.holderName.trim() || undefined,
                holderEmail: draft.holderEmail.trim() || undefined,
                maxAssignments,
                notes: draft.notes.trim() || undefined,
            }),
        );
        setShowNewModal(false);
    };

    const buttons: any[] = [];
    buttons.push(
        <ParkingFilters
            filter={filter}
            floors={floors}
            onChangeFilters={onChangeFilter}
        />,
        <form
            autoComplete="off"
            onSubmit={(e) => e.preventDefault()}
            style={{ minWidth: "18rem" }}
        >
            <CustomSearchBar
                onChange={(e) => onSearchInput(e.target.value)}
                value={searchInput}
                placeholder={t("mod_parking_search_placeholder")}
                className="w-100"
            />
        </form>,
    );

    if (canManage) {
        buttons.push(
            <CapsuleButton
                text={t("mod_parking_new")}
                onClick={openNewModal}
                isEnabled={hasGroupConfigured}
                // A greyed-out button with no explanation reads as a bug. On
                // hover, say what is missing and where to fix it.
                tooltip={
                    hasGroupConfigured
                        ? undefined
                        : t("mod_parking_new_disabled_no_group")
                }
                boostedToolTip={!hasGroupConfigured}
                // Fixed id: `boostedToolTip` otherwise mints a fresh uuid on
                // every render, and this view repaints on each keystroke of
                // the search box — which would drop the tooltip mid-hover.
                tooltipProps={
                    hasGroupConfigured
                        ? undefined
                        : { id: "parking-new-no-group" }
                }
            />,
            <>
                <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    style={{ display: "none" }}
                    onChange={handleCsvFileChange}
                />
                <Dropdown>
                    <Dropdown.Toggle as={SettingsDropdownToggle}>
                        <CapsuleButton
                            icon={settingsIcon}
                            tooltip={t("mod_parking_settings")}
                            boostedToolTip
                            classNameExtra="parking-settings-button"
                        />
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end" style={{ margin: 0 }}>
                        <Dropdown.Item
                            onClick={() => setShowImportModal(true)}
                            disabled={isImporting || !hasGroupConfigured}
                        >
                            {t("mod_parking_import_csv")}
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() =>
                                setGroupModalSignal((n) => (n ?? 0) + 1)
                            }
                        >
                            {t("mod_parking_settings_change_group")}
                        </Dropdown.Item>
                        <Dropdown.Item
                            className="text-danger"
                            onClick={() => setShowClearConfirm(true)}
                        >
                            {t("mod_parking_clear_all")}
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </>,
        );
    }

    return (
        <>
            <MainPageWithButtons
                pageTitle={t("mod_parking_spots_count", { count: totalCount })}
                size={"UNLIMITED"}
                buttons={buttons}
                content={
                    <>
                        {canManage && configLoaded && (
                            <ParkingGroupConfig
                                hideBannerWhenConfigured
                                openSignal={groupModalSignal}
                                groups={invitationGroups}
                                rooms={rooms}
                                selectedGroupUid={invitationGroupUid}
                                onChange={(uid) => {
                                    if (!locationUid) return;
                                    dispatch(
                                        parkingActions.setInvitationGroup({
                                            locationUid,
                                            invitationGroupUid: uid,
                                        }),
                                    );
                                }}
                                onCreate={async ({
                                    name,
                                    accessScope,
                                    roomUids,
                                }) => {
                                    if (!locationUid) return;
                                    await dispatch(
                                        parkingActions.createAndSetGroup({
                                            locationUid,
                                            name,
                                            accessScope,
                                            roomUids,
                                        }),
                                    );
                                }}
                            />
                        )}
                        {isRequesting || (canManage && !configLoaded) ? (
                            <WaitSpinner />
                        ) : totalCount === 0 ? (
                            canManage && !hasActiveFilters ? (
                                <div className="d-flex align-items-center justify-content-center p-4">
                                    <div
                                        className="text-center bg-white rounded shadow-sm p-5"
                                        style={{ maxWidth: "32rem" }}
                                    >
                                        {hasGroupConfigured ? (
                                            <>
                                                <h3 className="mb-2">
                                                    {t("mod_parking_empty_title")}
                                                </h3>
                                                <p className="text-muted mb-4">
                                                    {t("mod_parking_empty_desc")}
                                                </p>
                                                <div className="d-flex justify-content-center flex-wrap">
                                                    <div className="m-1">
                                                        <CapsuleButton
                                                            text={t(
                                                                "mod_parking_empty_create",
                                                            )}
                                                            onClick={openNewModal}
                                                        />
                                                    </div>
                                                    <div className="m-1">
                                                        <CapsuleButton
                                                            text={t(
                                                                "mod_parking_empty_import",
                                                            )}
                                                            onClick={() =>
                                                                setShowImportModal(
                                                                    true,
                                                                )
                                                            }
                                                            isEnabled={
                                                                !isImporting
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <h3 className="mb-2">
                                                    {t(
                                                        "mod_parking_empty_group_title",
                                                    )}
                                                </h3>
                                                <p className="text-muted mb-4">
                                                    {t(
                                                        "mod_parking_empty_group_desc",
                                                    )}
                                                </p>
                                                <div className="d-flex justify-content-center">
                                                    <CapsuleButton
                                                        text={t(
                                                            "mod_parking_empty_group_cta",
                                                        )}
                                                        onClick={() =>
                                                            setGroupModalSignal(
                                                                (n) =>
                                                                    (n ?? 0) + 1,
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="d-flex align-items-center justify-content-center p-4">
                                    <h3>{t("mod_parking_empty_filtered")}</h3>
                                </div>
                            )
                        ) : (
                            <div className="d-flex flex-column-reverse h-100">
                                {totalCount > pageSize && (
                                    <div className="bg-white pt-3 py-2">
                                        <Paginator
                                            pageSize={pageSize}
                                            total={totalCount}
                                            pageOffset={offset}
                                            onChangePage={onChangePage}
                                        />
                                    </div>
                                )}
                                <div className="position-relative h-100">
                                    <div className="position-absolute top-0 left-0 w-100 overflow-auto h-100">
                                        <div ref={listRef} className="c-default-grid">
                                            {spots.map((spot) => {
                                                // Hide the holder name/email on a spot when the viewer
                                                // IS that holder. The backend already restricts a
                                                // non-admin caller to spots where they are the holder
                                                // (see /parking/spot/list authorization), so in practice
                                                // every spot a non-admin sees satisfies the equality
                                                // check below — but we keep the per-spot comparison to
                                                // stay correct if the API ever starts returning spots
                                                // where the caller is only an assignee.
                                                const isHolderView =
                                                    !isAdmin &&
                                                    !!currentUserEmail &&
                                                    spot.holderEmail ===
                                                        currentUserEmail;
                                                return (
                                                    <SingleParkingSpot
                                                        key={spot.uid}
                                                        spot={spot}
                                                        isSelected={
                                                            spot.uid ===
                                                            selectedUid
                                                        }
                                                        isHolderView={
                                                            isHolderView
                                                        }
                                                        onClick={() =>
                                                            dispatch(
                                                                parkingActions.selectSpot(
                                                                    spot.uid,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                }
            />
            <CustomModal
                show={showImportModal}
                centered
                onHide={() => setShowImportModal(false)}
                title={t("mod_parking_import_csv")}
                content={
                    <div>
                        <p>{t("mod_parking_import_intro")}</p>
                        <p className="mb-1">
                            {t("mod_parking_import_format_desc")}
                        </p>
                        <p className="mb-1">
                            {t("mod_parking_import_one_per_spot")}
                        </p>
                        <p className="mb-1">
                            {t("mod_parking_import_max_users_desc")}
                        </p>
                        <pre className="c-bg-grey-240 p-2 rounded small mb-2">
{`holder,email,planta,numero,max_usuarios
"EJEMPLO EMPRESA SL",admin@ejemplo.com,1,201,3
"EJEMPLO EMPRESA SL",admin@ejemplo.com,1,202,1`}
                        </pre>
                        <a
                            href="/parking-sample-formato-a.csv"
                            download
                            className="small"
                        >
                            {t("mod_parking_import_download_sample")}
                        </a>

                        <p className="text-muted font-italic small mt-4 mb-0">
                            {t("mod_parking_import_notes")}
                        </p>
                    </div>
                }
                footer={
                    <>
                        <CapsuleButton
                            text={t("global_cancel")}
                            onClick={() => setShowImportModal(false)}
                        />
                        <CapsuleButton
                            text={t("mod_parking_import_select_file")}
                            onClick={() => csvInputRef.current?.click()}
                        />
                    </>
                }
            />
            <CustomModal
                show={!!previewRows}
                centered
                size="lg"
                onHide={closePreview}
                title={t("mod_parking_import_preview_title")}
                content={
                    previewRows && (
                        <div>
                            <p>{t("mod_parking_import_preview_intro")}</p>
                            {previewUnparsed > 0 && (
                                <p className="text-muted small mb-2">
                                    {t("mod_parking_import_unparsed", {
                                        count: previewUnparsed,
                                    })}
                                </p>
                            )}
                            <div
                                style={{
                                    maxHeight: "50vh",
                                    overflowY: "auto",
                                }}
                            >
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th>{t("mod_parking_floor")}</th>
                                            <th>{t("mod_parking_number")}</th>
                                            <th>{t("mod_parking_holder")}</th>
                                            <th>{t("global_email")}</th>
                                            <th>
                                                {t(
                                                    "mod_parking_max_assignments",
                                                )}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewRows.map((row, i) => (
                                            <tr key={i}>
                                                <td>{row.planta}</td>
                                                <td>{row.numero}</td>
                                                <td>{row.holder}</td>
                                                <td>{row.email}</td>
                                                <td>
                                                    {row.maxAssignments ?? 1}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )
                }
                footer={
                    <>
                        <CapsuleButton
                            text={t("global_cancel")}
                            onClick={closePreview}
                        />
                        <CapsuleButton
                            text={t(
                                "mod_parking_import_preview_confirm",
                                { count: previewRows?.length ?? 0 },
                            )}
                            onClick={confirmPreviewImport}
                        />
                    </>
                }
            />
            <CustomModal
                show={isImporting}
                centered
                onHide={() => {}}
                title={t("mod_parking_import_csv")}
                content={
                    <div className="d-flex flex-column align-items-center py-3">
                        <WaitSpinner />
                        <p className="mt-3 mb-0">
                            {t("mod_parking_import_in_progress")}
                        </p>
                    </div>
                }
            />
            <CustomModal
                show={!!importResult}
                centered
                onHide={() => setImportResult(null)}
                title={t("mod_parking_import_csv")}
                content={
                    importResult && (
                        <>
                            <ul className="mb-0 pl-3">
                                <li>
                                    {t("mod_parking_import_created", {
                                        count: importResult.created,
                                    })}
                                </li>
                                {importResult.reused > 0 && (
                                    <li>
                                        {t("mod_parking_import_reused", {
                                            count: importResult.reused,
                                        })}
                                    </li>
                                )}
                                <li>
                                    {t("mod_parking_import_skipped", {
                                        count: importResult.skipped,
                                    })}
                                </li>
                                <li>
                                    {t("mod_parking_import_errors", {
                                        count: importResult.errors,
                                    })}
                                </li>
                                {importResult.unparsed > 0 && (
                                    <li>
                                        {t("mod_parking_import_unparsed", {
                                            count: importResult.unparsed,
                                        })}
                                    </li>
                                )}
                            </ul>
                            {!!importResult.warning_rows?.length && (
                                <div className="mt-3">
                                    <div className="font-weight-bold small mb-1">
                                        {t("mod_parking_import_warnings_title")}
                                    </div>
                                    <ul className="mb-0 pl-3 small">
                                        {importResult.warning_rows.map(
                                            (w, i) => (
                                                <li key={i}>
                                                    {t(
                                                        "mod_parking_import_warning_row",
                                                        {
                                                            row: w.row_index + 1,
                                                        },
                                                    )}
                                                    {": "}
                                                    {t(
                                                        `mod_parking_warning_${w.code}`,
                                                        {
                                                            defaultValue:
                                                                w.message ||
                                                                w.code,
                                                        },
                                                    )}
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                </div>
                            )}
                            {!!importResult.error_rows?.length && (
                                <div className="mt-3">
                                    <div className="font-weight-bold small mb-1">
                                        {t("mod_parking_import_errors_title")}
                                    </div>
                                    <ul className="mb-0 pl-3 small text-danger">
                                        {importResult.error_rows.map((e, i) => (
                                            <li key={i}>
                                                {t(
                                                    "mod_parking_import_warning_row",
                                                    {
                                                        row: e.row_index + 1,
                                                    },
                                                )}
                                                {": "}
                                                {e.message || e.code}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )
                }
                footer={
                    <CapsuleButton
                        text={t("global_ok")}
                        onClick={() => setImportResult(null)}
                    />
                }
            />
            <CustomModal
                show={showClearConfirm}
                centered
                onHide={() => setShowClearConfirm(false)}
                title={t("mod_parking_clear_all")}
                content={
                    <div>
                        <p>{t("mod_parking_clear_all_confirm")}</p>
                        <p className="font-italic mb-0 text-muted">
                            {t("global_cannot_undo")}
                        </p>
                    </div>
                }
                footer={
                    <>
                        <CapsuleButton
                            text={t("global_cancel")}
                            onClick={() => setShowClearConfirm(false)}
                        />
                        <CapsuleButton
                            style="DANGER"
                            text={t("mod_parking_clear_all")}
                            onClick={() => {
                                if (locationUid) {
                                    dispatch(
                                        parkingActions.clearAll(locationUid),
                                    );
                                }
                                setShowClearConfirm(false);
                            }}
                        />
                    </>
                }
            />
            <CustomModal
                show={showNewModal}
                centered
                onHide={() => setShowNewModal(false)}
                title={t("mod_parking_new")}
                content={
                    <NewParkingSpot
                        floors={floors}
                        draft={draft}
                        onChange={(patch) =>
                            setDraft((prev) => ({ ...prev, ...patch }))
                        }
                    />
                }
                footer={
                    <>
                        <CapsuleButton
                            style="DANGER"
                            text={t("global_cancel")}
                            onClick={() => setShowNewModal(false)}
                        />
                        <CapsuleButton
                            text={t("global_create")}
                            isEnabled={isDraftValid}
                            onClick={submitNewSpot}
                        />
                    </>
                }
            />
        </>
    );
}
